import { Col, Form, Radio, RadioChangeEvent, Row, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import debounce from "just-debounce-it";
import { useAuth } from '../../../../context/auth';
import { useContextUsers } from '../../../../context/users';
import { ISettingItem, IUserSettings, useChangeDefaultReportType } from '../../../../hooks/users';
import { JsonResult } from '../../../../types';
import SettingsList from './SettingsList';
import { REPORT_TYPE_OPTIONS } from '../../Reports/constants';
import { EnumReportTypes } from '../../../../types/reports';

import './style.less';
import ClosingCostList from './ClosingCostList';
import RichTextEditor from '../../../Common/RichTextEditor';

type TextType = 'closingText' | 'welcome_message';

const Settings = () => {
  const { user: { id } } = useAuth();

  const {
    settings,
    setSettings,
    getSettings,
    updateSettings,
    settingsLoading,
    profile,
    setProfile,
    updateProfile,
  } = useContextUsers();

  const debouncedHandleUpdateTableView = useRef(
    debounce((rows: ISettingItem[], reportType: EnumReportTypes) => {
      handleUpdate('tableView', rows, reportType);
    }, 700)
  ).current;

  const debouncedHandleUpdateReportView = useRef(
    debounce((rows: ISettingItem[], reportType: EnumReportTypes) => {
      handleUpdate('reportView', rows, reportType)
    }, 700)
  ).current;

  const [defaultReportType, setDefaultReportType] = useState<EnumReportTypes>();
  const updateUserDefaultReportType = useChangeDefaultReportType();

  useEffect(() => {
    if (profile && getSettings && !settings) {
      getSettings(profile?.reportDefaultType as EnumReportTypes);
    }
  }, [profile, settings]);

  useEffect(() => {
    if (profile) {
      setDefaultReportType(profile?.reportDefaultType);
    }
  }, [profile]);

  const handleUpdate = (type: string, rows: ISettingItem[], reportType: EnumReportTypes) => {
    setSettings?.((prev) => {
      const data = { ...prev, [type]: rows }

      updateSettings?.({ ...data, reportType }, id);

      return data as IUserSettings;
    })
  };

  const handleUpdateProfile = (params: JsonResult) => {
    if (!profile || !updateProfile) return;

    const { email } = profile;

    updateProfile({ email, ...params }, id);
  };

  const handleTextChange = (textType: TextType, htmlContent: string) => {
    if (textType === 'closingText') {
      handleUpdateProfile({ closingText: htmlContent });
    } else if (textType === 'welcome_message') {
      handleUpdateProfile({ welcome_message: htmlContent });
    }
  };

  const debouncedHandleTextChange = debounce((textType: TextType, htmlContent: string) => {
    handleTextChange(textType, htmlContent);
  }, 1000);

  const onDefaultTypeChange = ({ target }: RadioChangeEvent) => {
    const value = target.value as EnumReportTypes;

    setDefaultReportType(value);

    updateUserDefaultReportType.fetch({ reportType: value }).then(async () => {
      // @ts-ignore this correct
      setProfile({...profile, reportDefaultType: value})
      getSettings?.(value as EnumReportTypes);
    });
  };

  return (
    <Row gutter={[20, 20]}>
      <Col span={24}>
        <Space direction="vertical" size={20}>
          <Typography.Title level={3}>Default Report Type</Typography.Title>
          <Radio.Group
            options={REPORT_TYPE_OPTIONS}
            onChange={onDefaultTypeChange}
            value={defaultReportType}
            optionType="button"
            buttonStyle="solid"
            className="custom-radio-group"
          />
        </Space>
      </Col>
      <Col span={24}>
        <Row gutter={[20, 20]} align="top" wrap={false} style={{ display: 'flex', gap: '20px' }}>
          <Col flex="1 1 33%" style={{ minWidth: '300px' }}>
            <SettingsList
              loading={settingsLoading}
              draggable
              items={settings?.tableView || []}
              label="Default table view"
              description="Adjust how you view the report table, and you can also change their order by dragging them"
              handleUpdate={(rows: ISettingItem[]) =>
                debouncedHandleUpdateTableView(rows, defaultReportType as EnumReportTypes)}
            />
          </Col>
          <Col flex="1 1 33%" style={{ minWidth: '300px' }}>
            <SettingsList
              loading={settingsLoading}
              draggable
              items={settings?.reportView || []}
              label="Default report view"
              description="Adjust how you view the open report, and you can also change their order by dragging them"
              handleUpdate={(rows: ISettingItem[]) =>
                debouncedHandleUpdateReportView(rows, defaultReportType as EnumReportTypes)}
            />
          </Col>
          <Col flex="1 1 33%" style={{ minWidth: '300px' }}>
            <ClosingCostList />
          </Col>
        </Row>
      </Col>
      <Col span={24} style={{ marginTop: '20px' }}>
        <div className="flex-col gap-20">
          <div className="flex-col gap-4">
            <Typography.Text className="fs-0875 fw-500">Welcome Text Default</Typography.Text>
            {profile?.welcome_message && (
              <Form.Item name={['profile', 'welcome_message']} label="Enter Text" noStyle>
                <div className="custom-quill-editor">
                  <RichTextEditor
                    defaultValue={profile.welcome_message}
                    onChange={(content) => debouncedHandleTextChange('welcome_message', content)}
                    placeholder="Enter welcome text"
                  />
                </div>
              </Form.Item>
            )}
          </div>
          <div className="flex-col gap-4">
            <Typography.Text className="fs-0875 fw-500">Closing Text Default</Typography.Text>
            {profile?.closingText && (
              <Form.Item name={['profile', 'closingText']} label="Enter Text" noStyle>
                <div className="custom-quill-editor">
                  <RichTextEditor
                    defaultValue={profile.closingText}
                    onChange={(content) => debouncedHandleTextChange('closingText', content)}
                    placeholder="Enter closing text"
                  />
                </div>
              </Form.Item>
            )}
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default Settings;
